// HOVER PDF importer. Two-step flow:
//   1. Contractor drops a HOVER PDF → backend parses + returns a preview.
//   2. Preview modal shows extracted measurements + auto-generated lines
//      side-by-side; contractor clicks "Add to Estimate" → lines merge into
//      the current estimate (existing lines preserved, no duplicates by name).
//
// All measurements come from Claude via /api/estimates/hover-import. We render
// them read-only above the line list so the contractor can sanity-check before
// committing.
import React, { useRef, useState } from "react";
import { Upload, FileText, Check, X, Loader2, AlertTriangle, Printer, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import writeThrough from "@/lib/write_through";
import TakeoffReconCard from "@/components/estimate/TakeoffReconCard";
import { bakeWasteIntoLines, steerLpSoffit } from "@/lib/wasteLogic";
// Iter 78t — same elevation drawing component as AI Measure, fed from
// Phase 2 vision data (`per_elevation_siding_from_drawing`).
import ElevationDrawing from "@/components/estimate/ElevationDrawing";
import { buildElevationsFromHoverVision } from "@/lib/elevationBuilder";
import { printTakeoff } from "@/lib/printTakeoff";
import HoverInflightRuns from "@/components/estimate/HoverInflightRuns";
import GableCladdingPick from "@/components/estimate/GableCladdingPick";
import { BOARD_BATTEN_COLORS, SHAKE_COLORS } from "@/lib/colorOptions";

const KEY_LABELS = {
  siding_sqft: "Siding",
  siding_with_openings_sqft: "Siding (+10% small openings)",
  soffit_sqft: "Soffit",
  eaves_lf: "Eaves",
  rakes_lf: "Rakes",
  starter_lf: "Starter",
  outside_corner_count: "Outside corners",
  outside_corner_lf: "Outside corner LF",
  inside_corner_count: "Inside corners",
  inside_corner_lf: "Inside corner LF",
  opening_count: "Openings",
  window_count: "Windows",
  window_bottom_width_total_lf: "Window bottom widths",
  door_count: "Doors (total)",
  entry_door_count: "Entry doors",
  patio_door_count: "Patio doors",
  garage_door_count: "Garage doors",
  opening_perimeter_lf: "Opening perimeter",
  stories: "Stories",
  address: "Address",
};

// SINGLE FAMILY PER DOOR — LINE SURFACE (Howard ruled 2026-08-04): the tabs
// a restore may land, by estimate kind. lp_smart merges NOTHING through the
// frontend (THE CUT — the LP engine rebuilds server-side); the preview and
// the Apply count read the SAME filter, so the modal never advertises
// cross-family lines it must not land.
export const applicableRestoreLines = (kind, lines) => {
  const k = kind || "siding";
  if (k === "lp_smart") return [];
  const allowed = k === "windows" ? new Set(["windows"]) : new Set(["vinyl", "ascend"]);
  return (lines || []).filter((l) => allowed.has(l.tab || "vinyl"));
};

const TAB_LABELS = {
  vinyl: "Vinyl",
  ascend: "Ascend",
  lp_smart: "LP Smart",
  windows: "Windows",
};

const VERO_PRODUCT_TYPES = [
  "Vero Double Hung",
  "Vero 2-Lite Slider",
  "Vero 1-Lite Casement",
  // Iter 57t — Vero 3-Lite Slider + Vero Picture are frozen (pricing
  // unreliable). Drop them from the HOVER style dropdown so contractors
  // can't accidentally land an opening in a hidden Vero section.
];

// Mezzo has no Casement product type — fall back to DH on the Mezzo side
// when the Vero guess is Casement. Lets one edit drive both brands.
const VERO_TO_MEZZO = {
  "Vero Double Hung":     "Mezzo Double Hung",
  "Vero 2-Lite Slider":   "Mezzo 2-Lite Slider",
  "Vero 3-Lite Slider":   "Mezzo 3-Lite Slider",
  "Vero Picture":         "Mezzo Picture",
  "Vero 1-Lite Casement": "Mezzo Double Hung",
};

const UNIT_BY_KEY = (k) => {
  if (k.endsWith("_sqft")) return "ft²";
  if (k.endsWith("_lf")) return "LF";
  return "";
};

// Iter 79d — Async polling helper. Polls `/estimates/hover-import/status/
// {run_id}` every 2 s until the run flips to `done` or `error`. Throws
// on error or timeout (5 min hard cap). The optional `onStage` callback
// receives the current stage label so the calling component can keep a
// live progress message in sync.
const STAGE_LABEL = {
  "reading-pdf": "Reading the PDF…",
  "claude-mapping": "Reading report with Claude…",
  "building-lines": "Mapping to catalog…",
  "vision-verify": "Verifying with vision…",
  done: "Done",
  error: "Error",
};

export async function pollHoverImportStatus(runId, onStage) {
  const POLL_INTERVAL_MS = 2000;
  const MAX_ELAPSED_MS = 5 * 60 * 1000;
  const start = Date.now();
  // Track the last stage we surfaced so we don't fire setState on every
  // poll iteration with the same value (would still re-render but
  // cheaper to skip).
  let lastStage = "";
  while (true) {
    if (Date.now() - start > MAX_ELAPSED_MS) {
      throw new Error(
        "HOVER import timed out after 5 minutes. The worker may still complete in the background — please retry."
      );
    }
    let resp;
    try {
      resp = await api.get(`/estimates/hover-import/status/${runId}`, { timeout: 15000 });
    } catch (e) {
      // Transient network blip — keep polling unless the status code is
      // a hard error (404 = run lost, 403 = not our run).
      const code = e?.response?.status;
      if (code === 404 || code === 403) throw e;
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      continue;
    }
    const { status, stage, result, error } = resp.data || {};
    if (stage && stage !== lastStage) {
      lastStage = stage;
      if (typeof onStage === "function") {
        onStage(STAGE_LABEL[stage] || stage);
      }
    }
    if (status === "done") return result;
    if (status === "error") throw new Error(error || "HOVER import failed");
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
}

export default function HoverImportButton({ est, update, save }) {
  const t = useT();
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  // Iter 79d — async status messages while the polling loop runs. e.g.
  // "Claude is mapping the report…" / "Verifying with vision…" — gives
  // the contractor live feedback instead of a frozen spinner.
  const [stage, setStage] = useState("");
  const [result, setResult] = useState(null);
  const [openings, setOpenings] = useState([]);
  const [applying, setApplying] = useState(false);
  // Slice 1 (ruled 2026-07-16): Hover import stops assuming lap. LP-kind
  // imports ASK for the siding profile when the estimate has no default —
  // silent lap on a B&B job is a wrong quote wearing a default.
  const [hoverRunId, setHoverRunId] = useState(null);
  // SEND-269 — the refusal CHIP (the toast is ephemeral; the chip stays).
  // A POST that got no response at all names that state here instead of
  // leaving a spinner that stopped.
  const [refusal, setRefusal] = useState(null);
  // SEND-269 — runs that are alive on the server with nobody watching:
  // EST-915610's import outlived the browser's clock and the client never
  // held its run_id. The estimate now ASKS — the list lives in its own
  // component (SEND-135 forbids an effect in this file).
  const [inflightKey, setInflightKey] = useState(0);
  const refreshInflight = () => setInflightKey((k) => k + 1);
  const [profile, setProfile] = useState(est?.default_siding_profile || null);
  const [showWarning, setShowWarning] = useState(false);
  // SEND-330 — one required pick on the upload: what are the gables? B&B or
  // shake. No default — the file picker stays locked until he picks.
  const [gablePick, setGablePick] = useState(null);
  // SEND-331 — after the carve he sets the gable colour, starter run and J
  // run (with its size) on the preview. Nothing is defaulted; a field left
  // blank stays flagged and lands nothing.
  const [gableColor, setGableColor] = useState("");
  const [gableStarterLf, setGableStarterLf] = useState("");
  const [gableJSize, setGableJSize] = useState("");
  const [gableJLf, setGableJLf] = useState("");
  // Ruled 2026-08-04 — PRESENTATION ONLY collapse state for the two
  // row-heavy modal sections (window openings + sanity warnings).
  // Collapsed is the DEFAULT. These two booleans are never read by
  // apply() — the applied payload is byte-identical open or closed.
  const [openingsOpen, setOpeningsOpen] = useState(false);
  const [warningsOpen, setWarningsOpen] = useState(false);
  // Iter 78n — when set, the preview modal was opened by "Restore HOVER
  // lines" (re-running the mapper against cached measurements) instead of
  // a fresh PDF read. Shown as a subtitle in the modal header so the
  // contractor knows no new LLM call was made.
  const [restoredAt, setRestoredAt] = useState(null);
  // SEND-135 (P0 MONEY BUG, Howard 2026-08-27 on EST-381546): the HOVER
  // door is now SOURCE-LOCKED BY ALLOW-LIST. The previous guard was a
  // DENY-list naming "blueprint" and "ai_photo" — and the photo apply
  // writes `_source: "photo"`, a string that list never named. So a
  // brand-new estimate that had only ever seen an AI PHOTO run grew a
  // "Restore HOVER Lines" button, and pressing it presented PHOTO
  // numbers under the words "cached HOVER measurements". A deny-list
  // cannot hold: every future door has to remember to add itself.
  // ONLY HOVER-SOURCED DATA IS RESTORABLE HERE. Legacy blobs with no
  // `_source` predate the stamp and stay HOVER (back-compat, unchanged).
  const cachedSource = est?.hover_measurements?._source;
  const cachedIsHover = !cachedSource || cachedSource === "hover";
  const hasAnyCached = !!(est?.hover_measurements
    && Object.keys(est.hover_measurements).length);
  const hasCached = hasAnyCached && cachedIsHover;
  // (Deep Verify retired 2026-07-29 — the straight-on elevation read is
  // the single verification pass; its warnings ride the same banner.)
  // SEALED DEFAULT (Howard, 2026-07-28 — production restore): the facade
  // default COMPOSES AT IMPORT — wall classes side; masonry classes
  // (brick/block/stone + stucco/metal per sealed rules) exclude with the
  // reason named. Informational, never a gate. Untouched picker sends NO
  // scope (backend sealed default is the one emitter); a touched picker
  // sends an explicit custom scope (LP door).
  const FACADE_EXCLUDED_CLASSES = {
    brick: "masonry", block: "masonry", stone: "masonry",
    stucco: "stucco rule", metal: "non-sided cladding",
  };
  const facadeSidedByDefault = (k) => !(k in FACADE_EXCLUDED_CLASSES);
  const [facadeInclude, setFacadeInclude] = useState({});

  const facadeBreakdown = result?.measurements?.facade_breakdown || {};
  const facadeMaterials = Object.entries(facadeBreakdown)
    .filter(([k, v]) => k.endsWith("_sqft") && Number(v) > 0)
    .map(([k, v]) => ({ key: k.replace(/_sqft$/, ""), sqft: Number(v) }));
  const hasFacadePicker =
    facadeMaterials.length > 0 && facadeMaterials.some((m) => m.key !== "siding");
  const facadePickerInteractive = est?.kind === "lp_smart";
  const facadeIncluded = (k) =>
    k === "siding" || (facadeInclude[k] ?? facadeSidedByDefault(k));
  // Hover waste unification (ruled 2026-07-20): the ruled 10% default is
  // WRITTEN into the estimate's visible Waste % field on import — the
  // field governs; nothing applies silently inside formulas.
  // RESTORE GUARD (ruled 2026-08-04 — race cleanup finding, EST-536665):
  // a RESTORE reads no new report — the estimate's OWN field (family-
  // defaulted by hover-lp-run, contractor-editable since) is the
  // governing spec value. The import-time generic prefill persisted in
  // cached measurements must never regress it (panels 138 → 117 was
  // this clobber). Fresh imports keep the measurement prefill.
  const wasteFieldPrefill = Number(
    (restoredAt ? est?.waste_pct : null)
    ?? result?.measurements?._waste_field_prefill_pct
    ?? 10
  );

  const upload = async (f) => {
    if (!f) return;
    // Iter 79c (Feb 2026): Howard's directive — force waste_pct = 0 on
    // every HOVER upload. HOVER's siding sqft already includes its own
    // waste (the report literally has a "+ Openings < 20ft² +10%" line),
    // so any contractor-side waste % on top is double-counting.
    //
    // The Waste % input on the SettingsRow stays editable so the
    // contractor can bump it up manually if they want extra (e.g.
    // a tear-off with a lot of cut waste) — but the auto-applied
    // saved default is no longer used for HOVER imports. The Blueprint
    // button keeps its own prompt-based UX since blueprint takeoffs
    // don't pre-bake waste.
    // 2026-08-11 send-4: the waste_pct=0 reset is optimistic — if the
    // /hover-import POST refuses, the reset must not survive the
    // refusal (otherwise the contractor sees waste=0 without an
    // upload landing). writeThrough enforces the rollback.
    const prevWaste = est?.waste_pct;
    const needReset = typeof update === "function"
      && Number(est?.waste_pct ?? 0) !== 0;
    setBusy(true);
    setResult(null);
    setOpenings([]);
    setFacadeInclude({});
    setStage("");
    let startResp;
    const wtResult = await writeThrough({
      applyOptimistic: needReset ? () => update({ waste_pct: 0 }) : null,
      rollback: needReset ? () => update({ waste_pct: prevWaste }) : () => {},
      apiCall: async () => {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("overhang_in", String(est?.overhang_in ?? 12));
        if (gablePick) fd.append("gable_cladding", gablePick);
        startResp = await api.post("/estimates/hover-import", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 30000,
        });
        return startResp;
      },
      refusalTestid: "hover-import-refusal-chip",
      setRefusal,
    });
    if (!wtResult.ok) { setBusy(false); refreshInflight(); return; }
    try {
      const { run_id: runId, status: startStatus, result: startResult } = startResp.data || {};
      // Defensive: if the backend ever falls back to the legacy sync
      // shape (e.g. during a partial deploy), treat the POST body as
      // the final result and skip polling.
      if (!runId || startResult || startStatus === "done") {
        const final = startResult || startResp.data;
        // DEFECT FIX (Jon Casile founding example, 2026-07-23): the run id
        // must reach apply() — without it the hover-lp-run materialize
        // branch is unreachable and the LP list keeps the profile-blind
        // default (Lap) regardless of the picker.
        setHoverRunId(runId || null);
        setResult(final);
        setOpenings(final?.vero_openings || []);
        setOpeningsOpen(false);
        setWarningsOpen(false);
      } else {
        const final = await pollHoverImportStatus(runId, setStage);
        setHoverRunId(runId);
        setResult(final);
        setOpenings(final?.vero_openings || []);
        setOpeningsOpen(false);
        setWarningsOpen(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Import failed";
      // Iter 78af — surface a more useful message when the failure is
      // an edge timeout (axios ECONNABORTED, Cloudflare 502/524, etc.)
      // vs a real backend rejection. With the Iter 79d async refactor
      // these timeouts should be rare — the POST is sub-second, and
      // polling each takes <1 s. If we still see one here, it's
      // probably the initial PDF-text-extract on a huge PDF.
      const isTimeout =
        /timeout|502|503|504|524|bad gateway|gateway timeout|network|aborted/i.test(detail) ||
        e?.code === "ECONNABORTED";
      toast.error(
        isTimeout
          ? "HOVER import is taking longer than expected — please retry. If this PDF is unusually large (>15 MB) try splitting it."
          : detail
      );
      setRefusal({
        state: detail,
        wayOut: ("Nothing was imported. If the import is still running it is "
                 + "listed below — resume it, or mark it failed."),
        testid: "hover-import-refusal-chip",
      });
    } finally {
      setBusy(false);
      setStage("");
      refreshInflight();
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // SEND-269 — ASK WHAT IS IN FLIGHT. A run whose client went away is
  // still running on the server; the strip names it and offers the two
  // honest ways out: watch it, or fail it by name.
  const resumeRun = async (runId) => {
    setBusy(true);
    setRefusal(null);
    setStage("");
    setResult(null);
    try {
      const final = await pollHoverImportStatus(runId, setStage);
      setHoverRunId(runId);
      setResult(final);
      setOpenings(final?.vero_openings || []);
      setOpeningsOpen(false);
      setWarningsOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Import failed";
      toast.error(detail);
      setRefusal({
        state: detail,
        wayOut: "Nothing was imported. Read the PDF again when you are ready.",
        testid: "hover-import-refusal-chip",
      });
    } finally {
      setBusy(false);
      setStage("");
      refreshInflight();
    }
  };

  // SEND-328 — the AI drawing check now runs on EVERY import (no button).
  // Its flags arrive inside result.warnings and render on the yellow banner
  // below, exactly like the sanity-check flags. Nothing to trigger by hand.

  const updateOpeningType = (id, productType) => {
    setOpenings((prev) =>
      prev.map((op) => (op.id === id ? { ...op, product_type: productType } : op))
    );
  };

  // Iter 78n — Restore HOVER lines. Re-runs the takeoff mapper against
  // the measurements already cached on `est.hover_measurements`, no new
  // PDF upload + no new LLM call (free + instant). Opens the same
  // preview modal as a fresh import so the contractor can review +
  // selectively re-apply auto-fills that were accidentally cleared.
  const restore = async () => {
    // SEND-135 — THE CLICK IS THE ONLY TRIGGER, AND THE SOURCE IS
    // CHECKED AT THE CLICK. This runs from the button and from nothing
    // else: there is no effect, no page-load path and no AI-Photo side
    // effect that can reach it. The source check is belt-and-braces
    // behind the allow-list above — if a future door ever writes into
    // the shared blob, the restore REFUSES BY NAME instead of dressing
    // that door's numbers as HOVER lines.
    if (!hasAnyCached) return;
    if (!cachedIsHover) {
      toast.error(
        `This estimate's measurements came from the ${String(cachedSource).toUpperCase()} door, `
        + "not a HOVER report. HOVER restore only ever re-derives THIS estimate's own HOVER "
        + "source — another lane's numbers are never presented as HOVER lines.",
        { duration: 9000 }
      );
      return;
    }
    setBusy(true);
    setResult(null);
    setOpenings([]);
    setOpeningsOpen(false);
    setWarningsOpen(false);
    setFacadeInclude({});
    try {
      const { data } = await api.post("/measure/map", {
        measurements: est.hover_measurements,
        // SEND-135 — the refusal is ENFORCED SERVER-SIDE too. The
        // mapper is shared with the photo/per-elevation doors, so it
        // cannot blanket-refuse; naming the lane we expect lets it
        // refuse a cross-lane restore even if a future UI forgets.
        expect_source: "hover",
        estimate_id: est?.id || null,
        // PROFILE OWNS ITS FAMILY (P0 regression, ruled 2026-07-24): on a
        // profile-mapped estimate the restore derives the MAPPED family
        // and returns zero_family_lines so residue from other families
        // is zeroed on apply — never both families on the money surface.
        profile: est?.default_siding_profile || null,
      });
      setResult(data);
      setOpenings(data.vero_openings || []);
      setRestoredAt(new Date());
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Restore failed");
    } finally {
      setBusy(false);
    }
  };

  const removeOpening = (id) => {
    setOpenings((prev) => prev.filter((op) => op.id !== id));
  };

  const apply = async () => {
    if (!result?.lines?.length && !openings.length) return;

    // SEND-331 — land the gable colour / starter / J the contractor set on
    // the preview BEFORE the merge. The backend converts feet→pieces the same
    // way every other accessory does and leaves any blank field flagged; it
    // reads/writes no estimate, so the real estimates stay untouched.
    let workingLines = result?.lines || [];
    const gableColorPatch = {};
    const hasGableScope = (result?.lines || []).some((l) => l.gable_scope);
    if (hasGableScope && (gablePick === "bnb" || gablePick === "shake")) {
      try {
        const { data: gs } = await api.post("/estimates/gable-settings", {
          gable_cladding: gablePick,
          lines: result.lines,
          color: gableColor || null,
          starter_lf: gableStarterLf === "" ? null : Number(gableStarterLf),
          j_size: gableJSize || null,
          j_lf: gableJLf === "" ? null : Number(gableJLf),
        });
        workingLines = gs.lines || result.lines;
        if (gs.color_field && gs.color) gableColorPatch[gs.color_field] = gs.color;
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Gable settings failed — lines unchanged");
      }
    }

    // ─── Split incoming data by which estimate-kind it belongs on ───────────
    // Source kind determines which slice stays on the current estimate vs
    // gets routed to the auto-paired estimate of the opposite kind.
    // Iter 78z++++ — LP Smart got its own workspace (Iter 73). Importing
    // a HOVER PDF on a siding estimate no longer creates LP rows; if a
    // contractor wants LP they import from the LP workspace directly.
    // Legacy siding estimates with LP qty > 0 still surface the LP tab
    // via the back-compat path in EstimateEditor.
    const srcKind = est.kind || "siding";
    const allLines = workingLines;
    // THE CUT — extended to the HOVER door (Howard ruled 2026-08-04: "an LP
    // door restores LP lines ONLY... the single-family rule covers the LINE
    // RESTORE, not just estimate creation"). lp_smart-kind estimates compose
    // through the LP engine (hover-lp-run below rebuilds the LP list
    // server-side) — this importer merges NO composition lines. siding-kind
    // keeps vinyl+ascend (dual-brand by design); windows-kind keeps windows.
    // Cross-family lines never merge, either direction; the retired pairing
    // residue (pairedLines/wastedPaired) is gone.
    const sourceLines = applicableRestoreLines(srcKind, allLines);
    // Vero openings always belong on a windows-kind estimate; Mezzo openings
    // mirror the same set so the contractor can quote both brands side-by-side.
    const sourceOpenings = srcKind === "windows" ? openings : [];
    const pairedOpenings = srcKind === "windows" ? [] : openings;
    // Build the parallel Mezzo array from the same edits the contractor made
    // on the Vero side — one edit drives both brands. Strip Vero-only fields
    // (sister_color, glass_*, premium_*) since MezzoOpening has a leaner shape.
    const veroToMezzo = (op) => ({
      id: op.id,
      product_type: VERO_TO_MEZZO[op.product_type] || "Mezzo Double Hung",
      label: op.label || "",
      width: op.width,
      height: op.height,
      qty: op.qty || 1,
      bucket_label: "",
      base_mat: 0,
      adders: [],
    });
    const sourceMezzoOpenings = srcKind === "windows" ? openings.map(veroToMezzo) : [];
    const pairedMezzoOpenings = srcKind === "windows" ? [] : openings.map(veroToMezzo);

    // ─── Merge SOURCE-side lines into the current estimate ─────────────────
    // Hover waste unification (ruled 2026-07-20): LP imports write the
    // ruled 10% into the estimate's visible Waste % field and bake it
    // into line qtys via the same raw_qty mechanism the SettingsRow knob
    // uses — the field governs; nothing applies silently inside formulas.
    // Vinyl/Ascend keep Iter 79c's 0% (HOVER sqft already includes its
    // own waste). Paired (windows) lines never carry siding waste.
    // WASTE — SEALED, ALL FAMILIES, ONE RULE (Howard, 2026-07-28): the
    // contractor's visible Waste % field, family-defaulted (lap/soffit 10 ·
    // shake 15 · B&B 30 · nickel gap 12), is ALWAYS applied into the
    // quantity — vinyl and Ascend included. The Iter 79c "reset to 0 —
    // HOVER's sqft already includes waste" convention is RETIRED: post
    // facade-composition the +Openings<20ft² figure no longer anchors, and
    // a Hover-supplied adder was never the contractor's field. Paired
    // (windows) lines never carry siding waste.
    // ONE RULE, ALL FAMILIES (sealed 2026-07-28): a single family-
    // defaulted prefill — on restores it resolves to the estimate's own
    // field (see wasteFieldPrefill), never a per-kind branch here.
    const wastePct = wasteFieldPrefill;
    const soffitType = est?.lp_soffit_type || "mix";
    const wastedSource = steerLpSoffit(bakeWasteIntoLines(sourceLines, wastePct), soffitType);
    const existing = est.lines || [];
    const keyOf = (l) => `${l.tab || "vinyl"}::${l.section}::${l.name}`;
    const byKey = new Map(existing.map((l, i) => [keyOf(l), i]));
    const nextLines = [...existing];
    let added = 0;
    let updated = 0;
    for (const ln of wastedSource) {
      const key = keyOf(ln);
      const idx = byKey.get(key);
      if (idx == null) {
        // Vinyl-conventions split lines inherit tier pricing from their
        // base SKU row via base_item (ruled 2026-07-18).
        const baseIdx = ln.base_item != null ? byKey.get(`${ln.tab || "vinyl"}::${ln.section}::${ln.base_item}`) : null;
        const baseRow = baseIdx != null ? existing[baseIdx] : null;
        nextLines.push({
          tab: ln.tab || "vinyl",
          section: ln.section,
          name: ln.name,
          unit: ln.unit,
          qty: ln.qty,
          raw_qty: ln.raw_qty ?? null,
          mat: baseRow ? (baseRow.mat ?? 0) : 0,
          lab: baseRow ? (baseRow.lab ?? 0) : 0,
        });
        added += 1;
      } else {
        const cur = nextLines[idx];
        if (cur.qty_src === "human") {
          // HUMAN QTY IS ABSOLUTE (R6 ruled 2026-07-31): re-import never
          // clobbers a hand-typed quantity — the fresh derived value is
          // stamped for the "yours · derived" chip instead.
          nextLines[idx] = { ...cur, derived_qty: ln.qty };
        } else {
          nextLines[idx] = {
            ...cur,
            qty: ln.qty,
            // Preserve raw_qty when present so future waste-% changes
            // recompute correctly. null clears prior raw_qty for items
            // that re-imported as non-cut-prone (rare).
            raw_qty: ln.raw_qty ?? null,
          };
        }
        updated += 1;
      }
    }
    // PROFILE OWNS ITS FAMILY (ruled 2026-07-24): zero every other siding
    // family's DERIVED rows named by the mapper. Human-typed quantities
    // (qty_src === "human") survive — mixed-material jobs are human
    // choices, never derivation residue.
    for (const zk of result?.zero_family_lines || []) {
      const idx = byKey.get(`${zk.tab || "vinyl"}::${zk.section}::${zk.name}`);
      if (idx != null && nextLines[idx].qty_src !== "human" && (nextLines[idx].qty || 0) > 0) {
        nextLines[idx] = { ...nextLines[idx], qty: 0, raw_qty: null };
      }
    }
    const nextOpenings = [
      ...(est.vero_openings || []),
      ...sourceOpenings.map(({ hover_id, ...op }) => op),
    ];
    const nextMezzoOpenings = [
      ...(est.mezzo_openings || []),
      ...sourceMezzoOpenings,
    ];

    // Iter 78t — merge any contractor nudges into the elevation drawings
    // and stash on hover_measurements._ai_elevations so the customer PDF
    // can render them. Same shape as the AI Measure path.
    let hoverMeasurementsWithDrawings = result?.measurements
      ? { ...result.measurements, _source: "hover" }
      : null;
    try {
      const elevs = buildElevationsFromHoverVision(result?.measurements || {});
      if (elevs.length) {
        const edits = result?.measurements?._ai_elevation_edits || {};
        const merged = elevs.map((e) => {
          const ee = edits[e.label] || {};
          const opEdits = ee.openings || {};
          return {
            ...e,
            roof_style: ee.roof_style || e.roof_style,
            openings: e.openings.map((op) =>
              opEdits[op.id]
                ? { ...op, x_pct: opEdits[op.id].x_pct, y_pct: opEdits[op.id].y_pct }
                : op
            ),
          };
        });
        hoverMeasurementsWithDrawings = {
          ...(result?.measurements || {}),
          _source: "hover",
          _ai_elevations: merged,
          _ai_elevations_by_source: {
            ...((result?.measurements || {})._ai_elevations_by_source
              || est?.hover_measurements?._ai_elevations_by_source
              || {}),
            hover: merged,
          },
        };
      }
    } catch {
      /* non-fatal */
    }

    update({
      lines: nextLines,
      vero_openings: nextOpenings,
      mezzo_openings: nextMezzoOpenings,
      hover_measurements: hoverMeasurementsWithDrawings,
      // Unification (ruled 2026-07-20; ALL FAMILIES sealed 2026-07-28):
      // the visible field always states what the quantities carry.
      waste_pct: wastePct,
      // SEND-331 — the gable colour lands on its OWN family field.
      ...gableColorPatch,
    });
    setApplying(true);
    try {
      if (save) {
        await save({
          ...est,
          lines: nextLines,
          vero_openings: nextOpenings,
          mezzo_openings: nextMezzoOpenings,
          hover_measurements: hoverMeasurementsWithDrawings,
          waste_pct: wastePct,
          ...gableColorPatch,
        });
      }

      // DOORS ARE SINGLE-FAMILY (Howard ruled 2026-08-03): this door writes
      // ONLY its own estimate. The paired-estimate routing was removed —
      // cross-family fill is post-September.
      const winNote = sourceOpenings.length ? ` + ${sourceOpenings.length} windows` : "";
      // The waste toast must state the value the field ACTUALLY carries —
      // on LP the pick's family default (B&B 30 · lap 10) is written
      // server-side and adopted below.
      let appliedWastePct = wastePct;
      toast.success(
        `Imported HOVER: ${added} new + ${updated} updated${winNote} · saved`
      );
      // Slice 1 — Hover→LP engine bridge: materialize the import as an
      // LP-native run at the chosen profile (mapping contract governs;
      // unmappable fields flag pending). LP-kind fresh imports only.
      if (srcKind === "lp_smart" && profile && !hoverRunId) {
        // Honest failure — never silently skip the profile mapping
        toast.error("Hover run id missing — the LP list could NOT be derived at the chosen profile. Re-run the import.");
      }
      if (srcKind === "lp_smart" && hoverRunId && profile) {
        try {
          // Facade scope from the picker — sent ONLY when the contractor
          // TOUCHED it; untouched = the backend composes the sealed
          // default (2026-07-28). Never a stale wrap-0 scope.
          let facadeScope = null;
          if (facadeMaterials.length && Object.keys(facadeInclude).length) {
            const excluded = {};
            let wrap = 0;
            for (const m of facadeMaterials) {
              if (facadeIncluded(m.key)) wrap += m.sqft;
              else excluded[m.key] = m.sqft;
            }
            facadeScope = { mode: "custom", wrap_sqft: wrap, excluded };
          }
          const { data: lpRun } = await api.post(`/estimates/${est.id}/hover-lp-run`, {
            hover_run_id: hoverRunId,
            profile,
            ...(facadeScope ? { facade_scope: facadeScope } : {}),
          });
          // NOTHING STALE LEFT BEHIND (Howard, sealed 2026-07-28 —
          // f3e7d728): the profile pick RE-DERIVES and the SERVER'S
          // materialized truth (rebuilt lines, family-defaulted waste
          // field, scoped measurements) becomes the client state. Before
          // this, the debounced autosave could replay the pre-pick
          // default-family merge over the finished rebuild (lap 514
          // standing, B&B panels 0 — the pick never reached the screen).
          if (Array.isArray(lpRun?.tab_lines) && lpRun.tab_lines.length) {
            const { data: freshEst } = await api.get(`/estimates/${est.id}`);
            appliedWastePct = Number(freshEst.waste_pct ?? wastePct);
            update({
              lines: freshEst.lines,
              waste_pct: freshEst.waste_pct,
              hover_measurements: freshEst.hover_measurements,
            });
          }
          toast.info(
            `LP list now derives from this Hover report (${profile === "board_batten" ? "Board & Batten" : profile}). ${lpRun.mapping_flags?.length || 0} mapping note(s) — see the Material List panel.`,
            { duration: 8000 }
          );
          setTimeout(() => window.location.reload(), 1200);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Hover→LP run failed — LP list unchanged");
        }
      }
      // Waste unification (ruled 2026-07-20; ALL FAMILIES sealed
      // 2026-07-28): the message states the value actually written to the
      // visible field — never a silent engine default, never a family
      // exception.
      toast.info(
        `Waste ${appliedWastePct}% (family default — visible + editable, including to 0) written into the Waste % field. Line qtys carry it; change the field to recompute.`,
        { duration: 8000 }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Saved locally but failed to persist — click Save");
    } finally {
      setApplying(false);
      setResult(null);
      setOpenings([]);
      setRestoredAt(null);
      // SEND-331 — clear the gable settings for the next import.
      setGableColor("");
      setGableStarterLf("");
      setGableJSize("");
      setGableJLf("");
    }
  };

  return (
    <div data-testid="hover-import" className="w-full flex flex-col gap-1.5">
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid="hover-import-input"
      />
      <button
        type="button"
        className="w-full justify-center px-3 py-1.5 bg-[var(--surface)] text-[var(--ai)] border border-[var(--ai)] hover:bg-[#FAF5FF] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
        onClick={() => setShowWarning(true)}
        disabled={busy}
        data-testid="hover-import-btn"
        title="Import a HOVER measurement report (.pdf)"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {busy ? (stage || t("intake.reading")) : t("intake.importHover")}
      </button>
      {/* Iter 78n — Restore HOVER lines. Only renders when this estimate has
          cached measurements from a previous import. Re-runs the mapper
          against those cached numbers — no new PDF, no new LLM call. */}
      {hasCached && (
        <button
          type="button"
          className="w-full justify-center px-3 py-1.5 bg-[var(--surface)] text-[#0369A1] border border-[#0EA5E9] hover:bg-[#F0F9FF] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
          onClick={restore}
          disabled={busy}
          data-testid="hover-restore-btn"
          title="Re-apply the auto-fills from the most recent HOVER import — no new upload needed"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
          {t("hov.restore")}
        </button>
      )}
      {/* SEND-135 — WHERE THE DOOR IS OFF, IT SAYS SO. A missing button
          with no reason is how a contractor ends up hunting for a
          restore that must not exist on this estimate. */}
      {hasAnyCached && !cachedIsHover && (
        <div
          className="text-[10px] leading-snug text-[var(--muted)] px-0.5"
          data-testid="hover-restore-off-reason"
        >
          No HOVER restore on this estimate — its measurements came from the{" "}
          <b className="uppercase">{String(cachedSource)}</b> door, not a HOVER report.
          Another lane&apos;s numbers are never restored as HOVER lines.
        </div>
      )}

      {/* SEND-269 — THE REFUSAL SAYS SO, AND IT STAYS. A toast that has
          faded is not a surface; this chip names the state and the way
          out until the contractor tries again. */}
      {refusal && (
        <div
          className="text-[10px] leading-snug border border-[#DC2626] bg-[#FEF2F2] text-[#7F1D1D] px-2 py-1.5"
          data-testid={refusal.testid || "hover-import-refusal-chip"}
        >
          <b className="uppercase">{refusal.state}</b>
          {refusal.wayOut ? <div className="mt-0.5">{refusal.wayOut}</div> : null}
        </div>
      )}

      {/* SEND-269 — RUNS ALIVE WITH NOBODY WATCHING. EST-915610's import
          outlived the browser's clock; the client never held its run_id
          and the estimate said nothing. Now it names them. */}
      <HoverInflightRuns
        refreshKey={inflightKey}
        busy={busy}
        onWatch={resumeRun}
        stageLabel={STAGE_LABEL}
      />

      {showWarning && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowWarning(false)}
          data-testid="hover-warning-backdrop"
        >
          <div
            className="bg-[var(--surface)] max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="hover-warning-modal"
          >
            <div className="bg-[var(--brand)] text-[var(--on-brand)] px-5 py-4 flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 flex-shrink-0" />
              <div className="font-heading text-lg">{t("hov.qtyVerifyTitle")}</div>
            </div>
            <div className="p-5">
              <p className="text-sm text-[#3F3F46] leading-relaxed">
                {t("hov.qtyVerifyBody")}
              </p>
              {/* SEND-330/333 — WHAT ARE THE GABLES? No default; he must
                  pick. Shared pick (GableCladdingPick) — one copy, both doors. */}
              <GableCladdingPick value={gablePick} onChange={setGablePick} />
            </div>
            <div className="border-t border-[var(--border)] px-5 py-4 flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 bg-[var(--surface)] text-[var(--ink-2)] border border-[var(--border)] hover:bg-[var(--bg-app)] text-sm font-bold uppercase tracking-wider"
                onClick={() => setShowWarning(false)}
                data-testid="hover-warning-cancel"
              >
                {t("hov.cancel")}
              </button>
              <button
                type="button"
                disabled={!gablePick}
                className="px-4 py-2 bg-[var(--brand)] text-[var(--on-brand)] border border-[var(--brand)] hover:bg-[var(--brand-hover)] text-sm font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={() => {
                  setShowWarning(false);
                  fileRef.current?.click();
                }}
                data-testid="hover-warning-agree"
                autoFocus
              >
                {t("hov.agree")}
              </button>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => { setResult(null); setOpenings([]); setRestoredAt(null); }}
          data-testid="hover-modal-backdrop"
        >
          <div
            className="bg-[var(--surface)] max-w-3xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="hover-modal"
          >
            <div className="bg-[var(--bar-bg)] text-white px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5" />
                <div>
                  <div className="font-heading text-lg">
                    {restoredAt ? "HOVER Lines Restored (Cached)" : "HOVER Report Imported"}
                  </div>
                  <div className="text-xs opacity-90 mt-0.5">
                    {restoredAt
                      ? `Source: cached HOVER measurements (no new LLM call) · re-run at ${restoredAt.toLocaleTimeString()}`
                      : "Review measurements and line items below — click Apply when ready"}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="text-white/90 hover:text-white"
                onClick={() => { setResult(null); setOpenings([]); setRestoredAt(null); }}
                aria-label="Close"
                data-testid="hover-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {/* Iter 78o — Phase 1 sanity-check warnings. Yellow banner
                  surfaces deterministic discrepancies the rule engine
                  caught (eaves×overhang vs soffit, rake/eave ratio,
                  opening-perim consistency, door-count integrity, corner
                  plausibility). Empty array = report looks consistent
                  (banner hidden). */}
              {Array.isArray(result.warnings) && result.warnings.length > 0 && (() => {
                // Fold rule (ruled 2026-08-04): info/warn heuristics fold
                // behind the count; any FUTURE blocking level (anything not
                // info/warn) stays visible even when collapsed — the fold
                // must never bury a flag that should stop the contractor.
                const blocking = result.warnings.filter(
                  (w) => w.level && w.level !== "info" && w.level !== "warn"
                );
                const shown = warningsOpen ? result.warnings : blocking;
                return (
                <div
                  className="border-b border-[#FCD34D] bg-[#FFFBEB] px-5 py-3"
                  data-testid="hover-warnings-banner"
                >
                  <button
                    type="button"
                    onClick={() => setWarningsOpen((o) => !o)}
                    className="w-full flex items-center gap-2 text-left"
                    data-testid="hover-warnings-toggle"
                  >
                    {warningsOpen ? (
                      <ChevronDown className="w-3.5 h-3.5 text-[var(--warning-text)] shrink-0" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-[var(--warning-text)] shrink-0" />
                    )}
                    <AlertTriangle className="w-4 h-4 text-[var(--warning-text)]" />
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--warning-text)]">
                      {t("hov.sanity", { n: result.warnings.length })}
                    </span>
                    {!warningsOpen && (
                      <span className="text-[10px] text-[var(--warning-text)]">{t("hov.tapToReview")}</span>
                    )}
                  </button>
                  {shown.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {shown.map((w) => (
                      <div
                        key={w.code}
                        className="text-[12px] text-[#78350F] leading-snug"
                        data-testid={`hover-warning-${w.code}`}
                      >
                        <div className="font-bold">⚠ {w.message}</div>
                        {w.detail && (
                          <div className="text-[10px] font-mono-num text-[var(--warning-text)] mt-0.5">
                            {w.detail}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  )}
                  {warningsOpen && (
                  <div className="text-[10px] text-[var(--warning-text)] mt-2 italic">
                    These are heuristic checks — review the elevation drawings to confirm before applying.
                  </div>
                  )}
                </div>
                );
              })()}
              {/* SEND-331 — GABLE SETTINGS. After a B&B/Shake carve he sets
                  the gable colour (its own palette), the starter run and the
                  J run (with its size) here. Any field left blank stays
                  flagged and lands nothing. */}
              {(result.lines || []).some((l) => l.gable_scope)
                && (gablePick === "bnb" || gablePick === "shake") && (
                <div
                  className="p-5 border-b border-[#FCD34D] bg-[#FFFBEB]"
                  data-testid="hover-gable-settings"
                >
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--warning-text)] mb-1">
                    Gable {gablePick === "bnb" ? "Board & Batten" : "Shake"} — colour, starter & J
                  </div>
                  <p className="text-[11px] text-[#78350F] leading-snug mb-3">
                    Set the gable colour, starter run and J run. The colour uses the{" "}
                    {gablePick === "bnb" ? "Board & Batten" : "Shake"} palette — nothing is
                    borrowed from the field siding. Feet convert to pieces the same way the
                    rest of the takeoff does. <strong>Leave one blank and that one stays flagged.</strong>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="font-bold uppercase tracking-wider text-[var(--ink-2)]">Gable colour</span>
                      <select
                        className="border border-[var(--border)] text-sm px-2 py-1.5 bg-[var(--surface)] font-semibold"
                        value={gableColor}
                        onChange={(e) => setGableColor(e.target.value)}
                        data-testid="hover-gable-color"
                      >
                        <option value="">— pick a colour —</option>
                        {(gablePick === "bnb" ? BOARD_BATTEN_COLORS : SHAKE_COLORS).map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="font-bold uppercase tracking-wider text-[var(--ink-2)]">Starter run (LF)</span>
                      <input
                        type="number"
                        min="0"
                        inputMode="decimal"
                        className="border border-[var(--border)] text-sm px-2 py-1.5 bg-[var(--surface)] font-mono-num"
                        value={gableStarterLf}
                        onChange={(e) => setGableStarterLf(e.target.value)}
                        placeholder="leave blank to stay flagged"
                        data-testid="hover-gable-starter-lf"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="font-bold uppercase tracking-wider text-[var(--ink-2)]">J size</span>
                      <div className="flex gap-2" data-testid="hover-gable-j-size">
                        {["3/4", "1/2"].map((sz) => (
                          <button
                            key={sz}
                            type="button"
                            onClick={() => setGableJSize((s) => (s === sz ? "" : sz))}
                            aria-pressed={gableJSize === sz}
                            data-testid={`hover-gable-j-size-${sz === "3/4" ? "three-quarter" : "half"}`}
                            className={`flex-1 px-3 py-1.5 text-sm font-bold uppercase tracking-wider border ${
                              gableJSize === sz
                                ? "bg-[var(--brand)] text-[var(--on-brand)] border-[var(--brand)]"
                                : "bg-[var(--surface)] text-[var(--ink-2)] border-[var(--border)] hover:bg-[var(--bg-app)]"
                            }`}
                          >
                            {sz}&quot;
                          </button>
                        ))}
                      </div>
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="font-bold uppercase tracking-wider text-[var(--ink-2)]">J run (LF)</span>
                      <input
                        type="number"
                        min="0"
                        inputMode="decimal"
                        className="border border-[var(--border)] text-sm px-2 py-1.5 bg-[var(--surface)] font-mono-num"
                        value={gableJLf}
                        onChange={(e) => setGableJLf(e.target.value)}
                        placeholder="pick a size + run, or stay flagged"
                        data-testid="hover-gable-j-lf"
                      />
                    </label>
                  </div>
                </div>
              )}
              {/* Measurements block */}
              {(() => {
                const elevs = buildElevationsFromHoverVision(result.measurements || {});
                if (!elevs.length) return null;
                const edits = result.measurements?._ai_elevation_edits || {};
                const merged = elevs.map((e) => {
                  const editsForElev = edits[e.label] || {};
                  const opEdits = editsForElev.openings || {};
                  return {
                    ...e,
                    roof_style: editsForElev.roof_style || e.roof_style,
                    openings: e.openings.map((op) =>
                      opEdits[op.id]
                        ? { ...op, x_pct: opEdits[op.id].x_pct, y_pct: opEdits[op.id].y_pct }
                        : op
                    ),
                  };
                });
                const handleNudge = (lbl) => (opId, xPct, yPct) => {
                  setResult((r) => {
                    if (!r) return r;
                    const cur = r.measurements?._ai_elevation_edits || {};
                    const ee = cur[lbl] || { openings: {}, roof_style: null };
                    return {
                      ...r,
                      measurements: {
                        ...r.measurements,
                        _ai_elevation_edits: {
                          ...cur,
                          [lbl]: { ...ee, openings: { ...(ee.openings || {}), [opId]: { x_pct: xPct, y_pct: yPct } } },
                        },
                      },
                    };
                  });
                };
                const handleRoof = (lbl) => (shape) => {
                  setResult((r) => {
                    if (!r) return r;
                    const cur = r.measurements?._ai_elevation_edits || {};
                    const ee = cur[lbl] || { openings: {}, roof_style: null };
                    return {
                      ...r,
                      measurements: {
                        ...r.measurements,
                        _ai_elevation_edits: { ...cur, [lbl]: { ...ee, roof_style: shape } },
                      },
                    };
                  });
                };
                return (
                  <div className="p-5 border-b border-[var(--border)] bg-[var(--surface)]" data-testid="hover-elevation-drawings">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--muted)] mb-1">
                      Elevation Drawings
                    </div>
                    <p className="text-[11px] text-[var(--ink-2)] mb-3">
                      Reconstructed from the Phase 2 vision pass on the HOVER PDF drawings. Drag any opening to reposition or click the Roof toggle to fix the shape.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {merged.map((e) => (
                        <ElevationDrawing
                          key={e.label}
                          elevation={e}
                          editable
                          compact
                          onOpeningMove={handleNudge(e.label)}
                          onRoofToggle={handleRoof(e.label)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })()}
              {/* Extracted Measurements block */}
              <div className="p-5 border-b border-[var(--border)] bg-[var(--surface-muted)]">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--muted)] mb-3">
                  Extracted Measurements
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {Object.entries(result.measurements || {})
                    .filter(([, v]) => v !== null && v !== undefined && v !== "" && typeof v !== "object")
                    .map(([k, v]) => (
                      <div key={k}>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
                          {KEY_LABELS[k] || k}
                        </div>
                        <div className="font-mono-num text-sm font-bold text-[var(--ink)] truncate" title={String(v)}>
                          {typeof v === "number"
                            ? `${v.toLocaleString()} ${UNIT_BY_KEY(k)}`.trim()
                            : v}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Iter 78 — Takeoff Reconciliation: raw → formula → ordered */}
              <TakeoffReconCard
                measurements={result.measurements || {}}
                lines={result.lines || []}
                wastePct={wasteFieldPrefill}
                wasteInFormula={false}
                kind={est?.kind || "siding"}
                lpSoffitType={est?.lp_soffit_type || "mix"}
              />

              {/* Facade scope — SEALED DEFAULT composes at import (Howard,
                  2026-07-28): wall classes side, masonry excludes with the
                  reason named. Informational, never a gate. */}
              {hasFacadePicker && (
                <div className="p-5 border-b border-[#FCD34D] bg-[#FFFBEB]" data-testid="hover-facade-scope-picker">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--warning-text)] mb-1">
                    Facade Scope — composed at import
                  </div>
                  <p className="text-[11px] text-[#78350F] leading-snug mb-3">
                    Every measured ft² is attributed automatically: <strong>wall classes side</strong>;{" "}
                    <strong>masonry classes (brick / block / stone / stucco / metal) exclude</strong>{" "}
                    with the reason named. Informational — never a gate.{" "}
                    {facadePickerInteractive
                      ? "Tap to change before applying."
                      : "Already composed into the draft lines below."}
                  </p>
                  <div className="space-y-1.5">
                    {facadeMaterials.map((m) => {
                      const included = facadeIncluded(m.key);
                      return (
                        <label
                          key={m.key}
                          className={`flex items-center gap-2 text-xs ${!facadePickerInteractive || m.key === "siding" ? "opacity-80" : "cursor-pointer"}`}
                          data-testid={`facade-scope-row-${m.key}`}
                        >
                          {facadePickerInteractive && (
                            <input
                              type="checkbox"
                              checked={included}
                              disabled={m.key === "siding"}
                              onChange={(e) =>
                                setFacadeInclude((prev) => ({ ...prev, [m.key]: e.target.checked }))
                              }
                              data-testid={`facade-scope-check-${m.key}`}
                            />
                          )}
                          <span className="font-bold capitalize text-[var(--ink)]">{m.key}</span>
                          <span className="font-mono-num text-[var(--ink-2)]">{m.sqft.toLocaleString()} ft²</span>
                          <span className="text-[10px] text-[var(--muted)]">
                            {m.key === "siding"
                              ? "always sides"
                              : included
                                ? (facadeSidedByDefault(m.key) ? "sided (wall class)" : "explicitly included")
                                : `excluded — ${FACADE_EXCLUDED_CLASSES[m.key] || "by choice"}`}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="text-[11px] font-mono-num text-[#78350F] mt-2" data-testid="facade-scope-summary">
                    Sided:{" "}
                    <strong>
                      {facadeMaterials
                        .filter((m) => facadeIncluded(m.key))
                        .reduce((s, m) => s + m.sqft, 0)
                        .toLocaleString()}{" "}
                      ft²
                    </strong>{" "}
                    · excluded{" "}
                    {facadeMaterials
                      .filter((m) => !facadeIncluded(m.key))
                      .reduce((s, m) => s + m.sqft, 0)
                      .toLocaleString()}{" "}
                    ft² of {facadeMaterials.reduce((s, m) => s + m.sqft, 0).toLocaleString()} ft² measured
                  </div>
                </div>
              )}

              {/* Vero Windows block — one row per HOVER opening with the
                  AI-guessed product type editable in a dropdown. Apply
                  appends these to est.vero_openings (VeroPanel resolves
                  bucket_label + price on next render). */}
              {openings.length > 0 && (() => {
                // "Needs review" = a row whose style guess didn't resolve to
                // a known Vero product type. Surfaced on the collapsed header
                // so the fold never hides a real problem.
                const needsReview = openings.filter(
                  (op) => !op.product_type || !VERO_PRODUCT_TYPES.includes(op.product_type)
                ).length;
                return (
                <div className="p-5 border-b border-[var(--border)]">
                  <button
                    type="button"
                    onClick={() => setOpeningsOpen((o) => !o)}
                    className={`w-full flex items-center gap-2 text-left ${openingsOpen ? "mb-3 pb-1 border-b border-[var(--border-strong)]" : ""}`}
                    data-testid="hover-openings-toggle"
                  >
                    {openingsOpen ? (
                      <ChevronDown className="w-3.5 h-3.5 text-[var(--brand-text)] shrink-0" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-[var(--brand-text)] shrink-0" />
                    )}
                    <span className="text-xs uppercase tracking-[0.18em] font-bold text-[var(--brand-text)]">
                      {t("hov.openingsTitle")}
                    </span>
                    <span className="text-[10px] text-[var(--muted)]" data-testid="hover-openings-count">
                      {openingsOpen
                        ? t("hov.openingsExpanded", { n: openings.length })
                        : t("hov.openingsTap", { n: openings.length })}
                    </span>
                    {needsReview > 0 && (
                      <span
                        className="text-[10px] font-bold text-[var(--warning-text)] bg-[#FFFBEB] border border-[#FCD34D] px-1.5 py-0.5"
                        data-testid="hover-openings-needs-review"
                      >
                        {t("hov.needsReview", { n: needsReview })}
                      </span>
                    )}
                  </button>
                  {openingsOpen && (
                  <>
                  <p className="text-[10px] text-[var(--ink-2)] leading-snug mb-2">
                    HOVER reports don&apos;t say if a window is double-hung, slider, casement, or picture — only the dimensions.
                    Each row below was auto-guessed from W × H. <strong>Confirm or change</strong> the style per opening; one pick fills <strong>both Mezzo and Vero</strong> tabs on the paired Windows estimate (Mezzo has no Casement, so Casement rows default to DH on the Mezzo side).
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted)]">
                        <th className="py-1 pr-2">HOVER ID</th>
                        <th className="py-1 pr-2 text-right">W</th>
                        <th className="py-1 pr-2 text-right">H</th>
                        <th className="py-1 pr-2 text-right">UI</th>
                        <th className="py-1 pr-2">Style (Vero product)</th>
                        <th className="py-1 pr-1 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {openings.map((op) => (
                        <tr
                          key={op.id}
                          className="border-b border-[var(--bg-app)]"
                          data-testid={`hover-opening-${op.hover_id || op.id}`}
                        >
                          <td className="py-1.5 pr-2 font-mono text-xs text-[var(--ink-2)]">
                            {op.hover_id || "—"}
                          </td>
                          <td className="py-1.5 pr-2 text-right font-mono-num text-xs">
                            {op.width}&quot;
                          </td>
                          <td className="py-1.5 pr-2 text-right font-mono-num text-xs">
                            {op.height}&quot;
                          </td>
                          <td className="py-1.5 pr-2 text-right font-mono-num text-xs text-[var(--ink-2)]">
                            {Math.round(op.width + op.height)}
                          </td>
                          <td className="py-1.5 pr-2">
                            <select
                              className="border border-[var(--border)] text-xs px-2 py-1 w-full bg-[var(--surface)] font-semibold"
                              value={op.product_type}
                              onChange={(e) => updateOpeningType(op.id, e.target.value)}
                              data-testid={`hover-opening-style-${op.hover_id || op.id}`}
                            >
                              {VERO_PRODUCT_TYPES.map((pt) => (
                                <option key={pt} value={pt}>{pt}</option>
                              ))}
                            </select>
                          </td>
                          <td className="py-1.5">
                            <button
                              type="button"
                              className="text-[var(--muted)] hover:text-[var(--danger-text)] p-1"
                              onClick={() => removeOpening(op.id)}
                              title="Skip this opening"
                              data-testid={`hover-opening-remove-${op.hover_id || op.id}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </>
                  )}
                </div>
                );
              })()}

              {/* Lines block — grouped by tab so the contractor can see at a
                  glance what each option will look like. */}
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--muted)] mb-3">
                  Auto-generated Line Items ({applicableRestoreLines(est?.kind, result.lines).length} across {Object.keys(TAB_LABELS).filter(t => applicableRestoreLines(est?.kind, result.lines).some(l => (l.tab || "vinyl") === t)).length} tabs)
                </div>
                {est?.kind === "lp_smart" && (
                  <div className="text-[11px] text-[var(--muted)] mb-3" data-testid="hover-lp-cut-note">
                    {t("hov.cutNote")}
                  </div>
                )}
                {applicableRestoreLines(est?.kind, result.lines).length ? (
                  ["vinyl", "ascend", "lp_smart", "windows"].map((tab) => {
                    const tabLines = applicableRestoreLines(est?.kind, result.lines).filter(
                      (l) => (l.tab || "vinyl") === tab
                    );
                    if (!tabLines.length) return null;
                    return (
                      <div key={tab} className="mb-5 last:mb-0">
                        <div className="flex items-center gap-2 mb-2 pb-1 border-b border-[var(--border-strong)]">
                          <span className="text-xs uppercase tracking-[0.18em] font-bold text-[var(--brand-text)]">
                            {TAB_LABELS[tab]} tab
                          </span>
                          <span className="text-[10px] text-[var(--muted)]">
                            ({tabLines.length} {tabLines.length === 1 ? "line" : "lines"})
                          </span>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted)]">
                              <th className="py-1 pr-3">Section</th>
                              <th className="py-1 pr-3">Item</th>
                              <th className="py-1 pr-3 text-right">Qty</th>
                              <th className="py-1 pr-3">Unit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tabLines.map((l) => (
                              <tr
                                key={`${l.tab}::${l.section}::${l.name}`}
                                className="border-b border-[var(--bg-app)]"
                              >
                                <td className="py-2 pr-3 text-xs text-[var(--ink-2)]">{l.section}</td>
                                <td className="py-2 pr-3">
                                  <div className="text-xs text-[var(--ink)]">{l.name}</div>
                                  {l.note && (
                                    <div className="text-[10px] text-[var(--muted)] mt-0.5">{l.note}</div>
                                  )}
                                </td>
                                <td className="py-2 pr-3 text-right font-mono-num text-sm font-bold">{l.qty}</td>
                                <td className="py-2 pr-3 text-xs text-[var(--ink-2)]">{l.unit}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-[var(--ink-2)]">
                    No line items generated — the report may be missing standard measurements.
                  </p>
                )}
              </div>
            </div>

            <div className="border-t border-[var(--border)] px-5 py-4 flex justify-between items-center">
              <div className="text-[10px] text-[var(--muted)]">
                Existing lines with matching names will have their qty updated. Windows are appended as new openings.
              </div>
              <div className="flex gap-2">
                {est?.kind === "lp_smart" && (
                  <div className="flex items-center gap-1.5 mr-2" data-testid="hover-profile-picker">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--ink-2)]">
                      Siding profile:
                    </span>
                    {[["lap", "Lap"], ["board_batten", "B&B"], ["shake", "Shake"], ["nickel_gap", "Nickel Gap"]].map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider border ${
                          profile === val
                            ? "bg-[var(--brand)] text-[var(--on-brand)] border-[var(--brand)]"
                            : "bg-[var(--surface)] text-[var(--ink-2)] border-[var(--border)]"
                        }`}
                        onClick={() => setProfile(val)}
                        data-testid={`hover-profile-${val}`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className="px-4 py-2 bg-[var(--surface)] text-[#0EA5E9] border border-[#0EA5E9] hover:bg-[#F0F9FF] text-sm font-bold uppercase tracking-wider flex items-center gap-1.5"
                  onClick={() =>
                    printTakeoff({
                      source: "HOVER",
                      measurements: result.measurements || {},
                      lines: result.lines || [],
                      openings,
                      est,
                      kind: est?.kind || "siding",
                    })
                  }
                  data-testid="hover-print-btn"
                  title="Print this takeoff preview"
                >
                  <Printer className="w-3.5 h-3.5" /> {t("hov.print")}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-[var(--surface)] text-[var(--ink-2)] border border-[var(--border)] hover:bg-[var(--bg-app)] text-sm font-bold uppercase tracking-wider"
                  onClick={() => { setResult(null); setOpenings([]); }}
                  data-testid="hover-cancel-btn"
                >
                  {t("hov.cancel")}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-[var(--brand)] text-[var(--on-brand)] border border-[var(--brand)] hover:bg-[var(--brand-hover)] text-sm font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
                  onClick={apply}
                  disabled={(!applicableRestoreLines(est?.kind, result.lines).length && !openings.length && est?.kind !== "lp_smart") || applying || (est?.kind === "lp_smart" && !profile)}
                  title={est?.kind === "lp_smart" && !profile ? "Pick the siding profile first — no silent default" : undefined}
                  data-testid="hover-apply-btn"
                >
                  {applying ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  {applying
                    ? "Saving…"
                    : est?.kind === "lp_smart"
                    ? t("hov.applyLp")
                    : `Apply ${applicableRestoreLines(est?.kind, result.lines).length} Lines${openings.length ? ` + ${openings.length} Windows` : ""} & Save`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
